import { ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { isNil, debounce, isEqual } from 'lodash-es';
import { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import { VIEW_MODE } from '@/common/define/common.define';
import { useViewModeStore } from './view-mode';
import { BASE_MENU_VALUE } from '../components/organisms/baseMenu/baseMenu.define';
import { useUserEnvStore } from './user-env';

export interface MenuHistory {
  [key: string]: string;
}

export interface SubMenuHistory {
  [key: string]: string;
}

const MENU_ORDER_ENV_KEY_PREFIX = 'gnb_menu_order';

export interface MainPanelInfo {
  isMainPanelExpanded: boolean; // 기본 GNB 좌우 확장 여부
  selectedCategory?: string;
  selectedSubMenu?: string;
}

export interface SubPanelInfo {
  openedSubPanelMenuInfo: MenuInfo | null;
  selectedSubPanelMenu?: string;
}

export const useBaseMenuStore = defineStore('baseMenuStore', () => {
  const viewModeStore = useViewModeStore();
  const { viewMode } = storeToRefs(viewModeStore);
  const userEnvStore = useUserEnvStore();
  const menuHistory = ref<MenuHistory>({});
  const subMenuHistory = ref<SubMenuHistory>({});
  const menuVisitRoute = ref<MenuInfo[]>([]);

  const mainPanelInfo = ref<MainPanelInfo>({ isMainPanelExpanded: false });
  const subPanelInfo = ref<SubPanelInfo>({ openedSubPanelMenuInfo: null });
  const isBaseMenuHidden = ref<boolean>(false);
  const previousSelectedSubMenu = ref<string | undefined>(undefined);

  // envKey 생성
  const getMenuOrderEnvKey = (): string => {
    return `${MENU_ORDER_ENV_KEY_PREFIX}_${viewMode.value}`;
  };

  // 메뉴 순서 저장 및 불러오기 (user-env 사용)
  const getMenuOrder = (): string[] => {
    const envKey = getMenuOrderEnvKey();
    const envData = userEnvStore.getEnvValue<{ menuOrder?: string[] }>(envKey);

    if (envData?.menuOrder && Array.isArray(envData.menuOrder)) {
      return envData.menuOrder;
    }

    return [];
  };

  // user-env에 메뉴 순서 저장
  const saveMenuOrderToEnv = debounce(async (order: string[]) => {
    const envKey = getMenuOrderEnvKey();
    const envData = userEnvStore.getEnvValue<{ menuOrder?: string[] }>(envKey) || {};

    if (!isEqual(envData.menuOrder, order)) {
      await userEnvStore.setEnvValue([{ key: envKey, value: { ...envData, menuOrder: order } }]);
    }
  }, 300);

  const updateMenuOrder = (order: string[]) => {
    saveMenuOrderToEnv(order);
  };

  // debounce 취소 함수 (메모리 누수 방지)
  const cancelPendingMenuOrderSave = () => {
    saveMenuOrderToEnv.cancel();
  };

  // 메뉴 순서 초기화 (원래 순서로 복원)
  const resetMenuOrder = async () => {
    const envKey = getMenuOrderEnvKey();
    const envData = userEnvStore.getEnvValue<{ menuOrder?: string[] }>(envKey) || {};

    if (envData.menuOrder) {
      const { menuOrder: _menuOrder, ...restEnvData } = envData;
      await userEnvStore.setEnvValue([{ key: envKey, value: restEnvData }]);
    }
  };

  const isShowMenuSearch = ref<boolean>(false);

  const getLastMenu = (category: string) => menuHistory.value?.[category] ?? '';
  const updateLastMenu = (category: string, lastMenu: string) => {
    menuHistory.value[category] = lastMenu;
  };

  const destroyLastMenu = () => {
    menuHistory.value = {};
  };

  const toggleBaseMenu = (value?: boolean) => {
    if (isNil(value)) {
      isBaseMenuHidden.value = !isBaseMenuHidden.value;
    } else {
      isBaseMenuHidden.value = value;
    }
  };

  // 메뉴 히스토리 키 생성 헬퍼 함수
  const createMenuHistoryKey = (category: string, subMenu?: string) => {
    return subMenu ? `${category}_${subMenu}` : category;
  };

  const selectSubPanelMenu = (selectedMenu: string) => {
    subPanelInfo.value.selectedSubPanelMenu = selectedMenu;

    if (selectedMenu) {
      previousSelectedSubMenu.value = undefined;
    }

    // 현재 선택된 메뉴에 대한 3depth 선택 상태 저장
    const currentCategory = mainPanelInfo.value.selectedCategory || menuVisitRoute.value[0]?.value;
    const currentSubMenu =
      mainPanelInfo.value.selectedSubMenu || subPanelInfo.value.openedSubPanelMenuInfo?.value;

    if (currentCategory && selectedMenu) {
      const historyKey = createMenuHistoryKey(currentCategory, currentSubMenu);
      subMenuHistory.value[historyKey] = selectedMenu;
    }
  };

  const getLastSubPanelMenu = (category: string, subMenu?: string) => {
    const historyKey = createMenuHistoryKey(category, subMenu);
    return subMenuHistory.value[historyKey] || '';
  };

  const selectCategory = ({
    menuValue,
    subMenuValue,
  }: {
    menuValue?: string;
    subMenuValue?: string;
  }) => {
    if (menuValue) {
      mainPanelInfo.value.selectedCategory = menuValue;
    }
    if (subMenuValue) {
      mainPanelInfo.value.selectedSubMenu = subMenuValue;
    }
  };

  const openSubPanel = (menuList: MenuInfo) => {
    // 우측 패널 열림

    // 이전 selectedSubMenu 저장 (다른 메뉴로 전환하는 경우만)
    if (subPanelInfo.value.openedSubPanelMenuInfo?.value !== menuList.value) {
      previousSelectedSubMenu.value = mainPanelInfo.value.selectedSubMenu;
      subPanelInfo.value.selectedSubPanelMenu = undefined;
    }

    mainPanelInfo.value.selectedSubMenu = menuList.value;
    if (subPanelInfo.value.openedSubPanelMenuInfo?.value === menuList.value) {
      return;
    }
    subPanelInfo.value.openedSubPanelMenuInfo = menuList;
  };

  const closeSubPanel = () => {
    // 우측 패널 닫힘

    // 3뎁스 메뉴를 선택하지 않고 닫는 경우, 이전 selectedSubMenu로 복원
    if (!subPanelInfo.value.selectedSubPanelMenu && previousSelectedSubMenu.value !== undefined) {
      mainPanelInfo.value.selectedSubMenu = previousSelectedSubMenu.value;
    }

    subPanelInfo.value.openedSubPanelMenuInfo = null;
    previousSelectedSubMenu.value = undefined;
  };

  const shrinkMainPanel = () => {
    // 확장된 메뉴 축소
    mainPanelInfo.value.isMainPanelExpanded = false;
  };

  const convertSubMenuForPADatabase = (subMenuValue?: string) => {
    if (!subMenuValue || viewMode.value !== VIEW_MODE.EXEMONE) return subMenuValue;
    if (subMenuValue.startsWith(BASE_MENU_VALUE.ORACLE)) {
      return BASE_MENU_VALUE.ORACLE;
    }
    if (subMenuValue.startsWith(BASE_MENU_VALUE.POSTGRESQL)) {
      return BASE_MENU_VALUE.POSTGRESQL;
    }
    if (subMenuValue.startsWith(BASE_MENU_VALUE.MYSQL)) {
      return BASE_MENU_VALUE.MYSQL;
    }
    if (subMenuValue.startsWith(BASE_MENU_VALUE.SQLSERVER)) {
      return BASE_MENU_VALUE.SQLSERVER;
    }
    return subMenuValue;
  };

  const toggleCategory = (menuValue?: string, subMenuValue?: string) => {
    // 특정 메뉴 클릭하면 열림
    if (!mainPanelInfo.value.isMainPanelExpanded) {
      // 메뉴 바 좁혀져 있을 때 확장
      mainPanelInfo.value.isMainPanelExpanded = true;

      if (
        !mainPanelInfo.value.selectedCategory ||
        mainPanelInfo.value.selectedCategory !== menuValue
      ) {
        // 열린 메뉴가 없거나 다른 메뉴 클릭할 때
        mainPanelInfo.value.selectedCategory = menuValue;
      }
    } else if (mainPanelInfo.value.selectedCategory === menuValue) {
      // 메뉴 바 확장되어 있고 같은 메뉴 한번 더 클릭할 때
      if (
        !(mainPanelInfo.value.selectedCategory === BASE_MENU_VALUE.HELP) &&
        !(mainPanelInfo.value.selectedCategory === BASE_MENU_VALUE.SETTINGS)
      ) {
        // 고객지원 메뉴, 프로필 메뉴는 여닫는 형태가 아니므로 delete 불필요
        delete mainPanelInfo.value.selectedCategory;
      }
      delete mainPanelInfo.value.selectedSubMenu;
    } else {
      // 메뉴 바 확장되어 있고 다른 메뉴 클릭할 때
      mainPanelInfo.value.selectedCategory = menuValue;
    }
    if (subMenuValue) {
      // 열린 메뉴 내부의 하위 메뉴 선택
      mainPanelInfo.value.selectedSubMenu = convertSubMenuForPADatabase(subMenuValue);
    }

    if (
      subPanelInfo.value.openedSubPanelMenuInfo &&
      menuValue !== BASE_MENU_VALUE.SETTINGS &&
      menuValue !== BASE_MENU_VALUE.HELP
    ) {
      closeSubPanel();
    }
  };

  const resetMenuInfo = () => {
    closeSubPanel();
    delete subPanelInfo.value.selectedSubPanelMenu;
    subMenuHistory.value = {};
    shrinkMainPanel();
    destroyLastMenu();
  };

  return {
    menuHistory,
    menuVisitRoute,

    getLastMenu,
    updateLastMenu,
    getLastSubPanelMenu,

    mainPanelInfo,
    subPanelInfo,

    isShowMenuSearch,

    selectSubPanelMenu,
    selectCategory,
    openSubPanel,
    closeSubPanel,

    toggleCategory,
    shrinkMainPanel,

    resetMenuInfo,

    isBaseMenuHidden,
    toggleBaseMenu,

    getMenuOrder,
    updateMenuOrder,
    resetMenuOrder,
    cancelPendingMenuOrderSave,
  };
});
